import React, { useEffect } from 'react'
import { Formik } from 'formik'
import { Tabs, Tab, Grid, Button } from '@mui/material'
import { type SolarProjectUpdateDto } from '../../../../api-schema-types/solar/SolarProjectDomainDtos'
import { RtkQueryHelper } from '../../../../api-services/shared/RtkQueryHelper'
import { useUpdateSolarProjectMutation } from '../../../../api-services/solar/solarApi'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { formatDateStringAsApiFormat } from '../../../../utility/formatters/DateFormatter'

import ErrorComponent from '../../../components/ui-widgets/ErrorComponent'
import { setNotification } from '../../../components/ui-widgets/uiWidgetsSlice'
import ViewForecasts from './ViewForecasts'
import EditIdentifierSummary from './EditIdentifierSummary'
import { selectSolarPlantTabIndex, setSolarPlantTabIndex } from './editSolarPlantSlice'
import ViewMonitoringAssets from './ViewMonitoringAssets'
import ViewPlantYieldRatios from './ViewPlantYieldRatios'

export interface EditSolarPlantFormProps {
  solarProject: SolarProjectUpdateDto
  triggerRefetch: () => void
}

export interface EditSolarPlantComponentProps {
  solarProject: SolarProjectUpdateDto
  triggerRefetch?: () => void
}

const EditSolarPlantForm: React.FC<EditSolarPlantFormProps> = ({
  solarProject,
  triggerRefetch
}: EditSolarPlantFormProps) => {
  const dispatch = useAppDispatch()

  const tabIndex = useAppSelector(selectSolarPlantTabIndex)

  const [apiUpdateProjectTrigger, updateResult] = useUpdateSolarProjectMutation()

  // ================================================================================================================
  //  FORM SETUP
  // ================================================================================================================

  const updateProjectOnSubmit = async (values: SolarProjectUpdateDto): Promise<void> => {
    const updateProject = { ...values }

    const updateLifecycleDates = { ...values.lifecycleDates }
    updateLifecycleDates.earlyEnergyDate = formatDateStringAsApiFormat(values.lifecycleDates.earlyEnergyDate)
    updateLifecycleDates.supplierCommercialOperationDate = formatDateStringAsApiFormat(values.lifecycleDates.supplierCommercialOperationDate)
    updateLifecycleDates.assetLeaseCommercialOperationDate = formatDateStringAsApiFormat(values.lifecycleDates.assetLeaseCommercialOperationDate)

    updateProject.lifecycleDates = updateLifecycleDates

    await apiUpdateProjectTrigger({ ...updateProject })
  }

  useEffect(() => {
    if (updateResult.isSuccess) {
      dispatch(setNotification({ open: true, severity: 'success', message: 'Project updated successfully' }))
    }

    if (updateResult.isError) {
      dispatch(setNotification({
        open: true,
        severity: 'error',
        message: RtkQueryHelper.mapResponseError(updateResult.error)
      }))
    }
  }, [updateResult.isSuccess, updateResult.isError])

  // ================================================================================================================
  //  UI Handlers
  // ================================================================================================================

  const handleTabSelected = (event: React.ChangeEvent<unknown>, newValue: number): void => {
    dispatch(setSolarPlantTabIndex(newValue))
  }

  const handleCancel = async (): Promise<void> => {
    dispatch(setNotification({
      open: true,
      severity: 'warning',
      message: 'Edit action cancelled. Project has been reloaded.'
    }))
    triggerRefetch()
  }

  // ================================================================================================================
  //  FORM MUTATION + RESULT
  // ================================================================================================================

  // const [apiUpdateProjectTrigger, updateResult] = useUpdateProjectMutation()

  // useEffect(() => {
  //   if (updateResult.isSuccess) {
  //     dispatch(setNotification({ open: true, severity: 'success', message: 'Project updated successfully' }))
  //   }
  //
  //   if (updateResult.isError) {
  //     dispatch(setNotification({
  //       open: true,
  //       severity: 'error',
  //       message: RtkQueryHelper.mapResponseError(updateResult.error)
  //     }))
  //   }
  // }, [updateResult.isSuccess, updateResult.isError])

  return (
    <>

      <Formik
        initialValues={solarProject}
        // validationSchema={validateAssetProjectUpdateDto}
        onSubmit={async (values, actions) => {
          await updateProjectOnSubmit(values)
        }}
      >
        {formikProps => (

          <form
            autoComplete={'off'}
            autoCorrect={'off'}
            onSubmit={formikProps.handleSubmit}
          >

            {!formikProps.isValid && (
              <Grid item md={12} sx={{ mt: 3 }}>
                <ErrorComponent title={'Form validation error'} message={JSON.stringify(formikProps.errors)}/>
              </Grid>
            )}

            <Tabs value={tabIndex} onChange={handleTabSelected} indicatorColor="primary" textColor="primary" centered>
              <Tab label="Identifier & Summary"/>
              <Tab label="Monitoring Assets"/>
              <Tab label="Forecasts"/>
              <Tab label="Plant Yield Ratios"/>
            </Tabs>

            {tabIndex === 0 && (
              <EditIdentifierSummary solarProject={solarProject}/>
            )}

            {tabIndex === 1 && (
              <ViewMonitoringAssets solarProject={solarProject}/>
            )}

            {tabIndex === 2 && (
              <ViewForecasts solarProject={solarProject}/>
            )}

            {tabIndex === 3 && (
              <ViewPlantYieldRatios solarProject={solarProject}/>
            )}

            <Grid item md={12} sx={{ mt: 3 }}>&nbsp;</Grid>

            <Grid container spacing={4}>
              <Grid item lg={9} sx={{ mt: 3 }}>
                <Button variant={'contained'} disabled={!formikProps.isValid} type={'submit'} sx={{ mr: 3 }}>
                  Update Project
                </Button>
                <Button type={'button'} variant={'text'} onClick={handleCancel}>
                  Cancel
                </Button>
              </Grid>
            </Grid>

          </form>

        )}
      </Formik>

    </>
  )
}

export default EditSolarPlantForm
